import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Route, Container, ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { SERVICES, IMAGES } from "../data/translations";
import { Reveal } from "../components/Reveal";

const badgeIcons = [CalendarDays, MapPin, Route, Container];

const Home = () => {
  const { lang, t } = useLanguage();

  return (
    <div data-testid="home-page">
      <section className="relative min-h-[92vh] flex flex-col justify-end overflow-hidden" data-testid="hero-section">
        <img src={IMAGES.hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/70" />
        <div className="container-site relative z-10 pt-36 md:pt-44 pb-14">
          <div className="max-w-2xl">
            <h1 className="text-xl sm:text-3xl leading-relaxed font-normal text-slate-200">{t.hero.subtitle}</h1>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/contact" data-testid="hero-quote-button" className="btn-primary">
                {t.hero.ctaPrimary} <ArrowRight size={16} />
              </Link>
              <Link to="/services" data-testid="hero-services-button" className="btn-outline">
                {t.hero.ctaSecondary}
              </Link>
            </div>
          </div>
        </div>
        <div className="relative z-10 border-t border-white/10 bg-black/50 backdrop-blur-sm">
          <div className="container-site grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4 py-5">
            {t.hero.badges.map((b, i) => {
              const Icon = badgeIcons[i];
              return (
                <div key={b} className="flex items-center gap-3" data-testid={`hero-badge-${i}`}>
                  <Icon size={18} className="text-brand shrink-0" />
                  <span className="text-xs sm:text-sm text-slate-200 font-medium">{b}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-pad bg-ink" data-testid="home-about-section">
        <div className="container-site grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-6">
            <p className="eyebrow">{t.homeAbout.eyebrow}</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-white leading-tight">{t.homeAbout.title}</h2>
            <p className="mt-6 text-slate-400 leading-relaxed">{t.homeAbout.p1}</p>
            <p className="mt-4 text-slate-400 leading-relaxed">{t.homeAbout.p2}</p>
            <ul className="mt-6 space-y-3">
              {t.homeAbout.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-slate-200">
                  <CheckCircle2 size={18} className="text-brand shrink-0 mt-0.5" /> {p}
                </li>
              ))}
            </ul>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="mt-8 inline-flex items-center gap-2 font-display font-semibold text-brand hover:text-orange-400 transition-colors"
            >
              {t.homeAbout.link} <ArrowUpRight size={18} />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative mb-8 lg:mb-0">
              <img
                src={IMAGES.truckSunset}
                alt="MVALOG fleet on the road"
                className="relative w-full h-[380px] sm:h-[440px] object-cover border-2 border-brand"
              />
              <div className="absolute -bottom-8 right-6 bg-ink-card border border-white/10 px-7 py-6 shadow-2xl" data-testid="home-about-stat">
                <p className="font-display text-4xl font-extrabold text-brand">{t.homeAbout.statValue}</p>
                <p className="mt-1 mini-label !text-slate-300">{t.homeAbout.statLabel}</p>
                <p className="mt-1 text-xs text-slate-500">{t.homeAbout.statSub}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-pad bg-ink-surface border-y border-white/5" data-testid="home-services-section">
        <div className="container-site">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <p className="eyebrow">{t.homeServices.eyebrow}</p>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-white">{t.homeServices.title}</h2>
                <p className="mt-3 text-slate-400 max-w-xl">{t.homeServices.subtitle}</p>
              </div>
              <Link
                to="/services"
                data-testid="home-services-link"
                className="inline-flex items-center gap-2 font-display font-semibold text-brand hover:text-orange-400 transition-colors shrink-0"
              >
                {t.homeServices.link} <ArrowUpRight size={18} />
              </Link>
            </div>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              const c = s[lang];
              return (
                <Reveal key={s.id} delay={(i % 3) * 0.1}>
                  <Link to="/services" data-testid={`home-service-card-${s.id}`} className="card-dark group block p-8 h-full hover:-translate-y-1">
                    <div className="w-12 h-12 bg-brand/10 border border-brand/30 flex items-center justify-center text-brand">
                      <Icon size={22} />
                    </div>
                    <h3 className="mt-6 font-display text-lg font-semibold text-white group-hover:text-brand transition-colors">{c.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-widest text-slate-500 group-hover:text-brand transition-colors">
                      {t.homeServices.cardLink} <ArrowRight size={14} />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden" data-testid="home-cta-section">
        <img src={IMAGES.truckDark} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/85" />
        <div className="container-site relative z-10 py-20 sm:py-24 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white">{t.ctaBand.title}</h2>
            <p className="mt-3 text-slate-300 max-w-lg">{t.ctaBand.subtitle}</p>
          </div>
          <Link to="/contact" data-testid="home-cta-button" className="btn-primary shrink-0">
            {t.ctaBand.button} <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
